"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.QmsService = void 0;
const service_1 = require("./service");
const qms_1 = require("../constants/qms");
const qmsMapper_1 = require("../utils/qmsMapper");
const qmsDialogs_1 = require("../utils/qmsDialogs");
const qmsPreprocess_1 = require("../bbcode/qmsPreprocess");
class QmsService extends service_1.Service {
    constructor(transport, auth) {
        super(transport, auth);
    }
    /**
     * List QMS dialogs, or search across all dialogs.
     * Command: `ql` — `[folder, offset, query]` for search (Android L0: `[0, 0, text]`).
     * @param query Optional search text (min 3 chars on server/client)
     * @param offset Pagination offset for search results
     */
    /**
     * Полный ответ `ql`: диалоги + «Сообщения 4PDA» (user id=0) отдельно.
     * Предпочтительная точка входа, если нужны оба списка без второго запроса.
     */
    async getDialogsPage(query, offset = 0) {
        const args = query ? [0, offset, query] : [];
        const response = await this.transport.send('ql', args);
        this.checkError(response, 'ql');
        return (0, qmsDialogs_1.splitForumNotifications)(qmsMapper_1.QmsMapper.mapDialogs(response.data));
    }
    /** Личные диалоги QMS (без system user id=0). */
    async getDialogs(query, offset = 0) {
        const page = await this.getDialogsPage(query, offset);
        return page.dialogs;
    }
    /**
     * Сообщения 4PDA — уведомления форума (system user в `ql`).
     * Темы берутся из общего списка; `ql [0]` не вызывается (см. API_Reference).
     */
    async getForumNotifications() {
        const page = await this.getDialogsPage();
        return page.forumNotifications;
    }
    /** Global QMS message search (`ql` folder 0). */
    async searchDialogs(query, offset = 0) {
        return this.getDialogs(query, offset);
    }
    async getThreads(userId) {
        const absUserId = Math.abs(userId);
        if (absUserId === qms_1.QMS_SYSTEM_USER_ID) {
            const notifications = await this.getForumNotifications();
            return notifications?.threads ?? [];
        }
        const response = await this.transport.send('ql', [absUserId]);
        this.checkError(response, 'ql');
        return qmsMapper_1.QmsMapper.mapThreads(response.data);
    }
    /**
     * Get chat messages (messages of a specific thread).
     * Command: "qt" (QMS_THREAD) - 29809 (0x7471)
     *
     * Pagination Logic (based on official client investigation):
     * 1. **Initial Load (Latest Messages)**:
     *    - `afterMessageId` (Offset) = 0
     *    - `limit` (Count) = -60 (Negative value fetches history backwards from now)
     *
     * 2. **Load History (Scroll Up)**:
     *    - `afterMessageId` = ID of the oldest message currently in view.
     *    - `limit` = -60 (Fetch 60 messages *older* than the offset).
     *
     * 3. **Load New Messages (Scroll Down / Search)**:
     *    - `afterMessageId` = ID of the newest message currently in view.
     *    - `limit` = 60 (Positive value fetches 60 messages *newer* than the offset).
     *
     * @param threadId Dialog ID (t_id)
     * @param afterMessageId Message ID used as offset. Use 0 for latest.
     * @param limit Number of messages. Positive = forward in time, Negative = backward in time.
     * @param query Search query (optional)
     */
    async getChat(threadId, afterMessageId = 0, limit = -60, query) {
        const args = [threadId, afterMessageId, limit];
        if (query !== undefined) {
            args.push(query);
        }
        const response = await this.transport.send('qt', args);
        this.checkError(response, 'qt');
        return qmsMapper_1.QmsMapper.mapChat(response.data);
    }
    /**
     * Jump to a specific message in normal (non-search) chat mode.
     * Mirrors Android M0.y: `qt [threadId, messageId, -1]` then `qt [threadId, anchorId, 60]`.
     */
    async jumpToMessage(threadId, messageId) {
        const locate = await this.transport.send('qt', [threadId, messageId, -1]);
        this.checkError(locate, 'qt');
        let anchorId = messageId;
        const messages = locate.data?.[13];
        if (Array.isArray(messages) && messages.length > 0) {
            const first = messages[0];
            if (Array.isArray(first) && typeof first[0] === 'number') {
                anchorId = first[0];
            }
        }
        const chat = await this.getChat(threadId, anchorId, 60);
        return { chat, anchorMessageId: anchorId, targetMessageId: messageId };
    }
    /**
     * Load all messages of a QMS thread (paginated `qt` backwards).
     */
    async getAllMessages(threadId, chunkSize = 60, onProgress) {
        const chunk = Math.max(1, Math.abs(chunkSize));
        const byId = new Map();
        let chat = await this.getChat(threadId, 0, -chunk);
        let total = chat.count;
        const absorb = (batch) => {
            for (const msg of batch.messages ?? []) {
                if (typeof msg.id === 'number') {
                    byId.set(msg.id, msg);
                }
            }
            onProgress?.(byId.size, total);
        };
        absorb(chat);
        let guard = 0;
        const maxIterations = Math.max(2, Math.ceil(total / chunk) + 2);
        while (byId.size < total && guard < maxIterations) {
            guard += 1;
            const oldest = [...byId.values()].reduce((min, msg) => (msg.id < min.id ? msg : min));
            const prevSize = byId.size;
            const batch = await this.getChat(threadId, oldest.id, -chunk);
            total = Math.max(total, batch.count);
            absorb(batch);
            if (byId.size === prevSize || !batch.messages?.length) {
                break;
            }
        }
        const messages = [...byId.values()].sort((a, b) => a.date - b.date || a.id - b.id);
        chat = { ...chat, messages, count: total };
        return { messages, chat };
    }
    /**
     * Get QMS blacklist.
     * Command: "qb" (QMS_BLACKLIST) - 25201
     * @returns Array of blocked users
     */
    async getBlacklist() {
        const response = await this.transport.send('qb', [0]);
        this.checkError(response, 'qb');
        return qmsMapper_1.QmsMapper.mapBlacklist(response.data);
    }
    /**
     * Add user to QMS blacklist.
     * Command: "qb" (QMS_BLACKLIST) - 25201
     * @param userId User ID to block
     */
    async addToBlacklist(userId) {
        const res = await this.transport.send('qb', [userId]);
        this.checkError(res, 'qb');
        return res;
    }
    /**
     * Remove user from QMS blacklist.
     * Command: "qb" (QMS_BLACKLIST) - 25201
     * @param userId User ID to unblock
     */
    async removeFromBlacklist(userId) {
        const res = await this.transport.send('qb', [-userId]);
        this.checkError(res, 'qb');
        return res;
    }
    /**
     * Send a message to an existing QMS thread.
     * Command: "qp" (QMS_POST) - 28785
     * @param threadId Thread ID to send message to
     * @param text Message text (BBCode supported)
     */
    async sendMessage(threadId, text, attachments = []) {
        const res = await this.transport.send('qp', [threadId, text, attachments]);
        this.checkError(res, 'qp');
        return res;
    }
    /**
     * Create a new QMS dialog.
     * Command: "qp" (QMS_POST) - 28785
     * @param recipientId User ID to start dialog with
     * @param title Dialog title
     * @param text First message text
     * @param attachments Attachments for the first message
     */
    async createDialog(recipientId, title, text, attachments = []) {
        // [req, "qp", 0, text, attachments, recipientId, title]
        const res = await this.transport.send('qp', [0, text, attachments, recipientId, title]);
        this.checkError(res, 'qp');
        return res;
    }
    /**
     * Delete a message or dialog thread.
     * Command: "qd" (QMS_DELETE) - 25713
     * @param threadId Dialog/Thread ID (use deleteDialogsWithUser to delete all threads with a user)
     * @param messageId Message ID to delete (0 to delete the entire thread)
     * @param deleteForAll Boolean flag (true = delete for partner too?)
     */
    async deleteMessage(threadId, messageId, deleteForAll = false) {
        const res = await this.transport.send('qd', [threadId, messageId, deleteForAll ? 1 : 0]);
        this.checkError(res, 'qd');
        return res;
    }
    /**
     * Delete all dialog threads with a specific user.
     * Command: "qd" (QMS_DELETE) - 25713 (with DialogID = 0)
     * @param userId Partner User ID
     * @param deleteForAll Boolean flag (true = delete for partner too?)
     */
    async deleteDialogsWithUser(userId, deleteForAll = false) {
        const res = await this.transport.send('qd', [0, userId, deleteForAll ? 1 : 0]);
        this.checkError(res, 'qd');
        return res;
    }
    async markAsRead(threadId, _lastMessageId) {
        // According to API_Reference.md and official app logs, QMS thread is marked as read
        // by resetting the unread count counter using command "mn" (UNREAD_COUNT)
        // with payload: [0, 1, threadId]
        const res = await this.transport.send('mn', [0, 1, threadId]);
        this.checkError(res, 'mn');
        return res;
    }
    /**
     * Send QMS activity status (Typing / Uploading).
     * Command: "ep" (EVENT_PUSH) - 28773
     * Payload: ["q<ThreadID>", 101, State, "u<PartnerID>"]
     * @param threadId Dialog ID
     * @param partnerId Partner User ID
     * @param type Activity type: 0 (Typing), 1 (Uploading)
     */
    async setActivity(threadId, partnerId, type) {
        const res = await this.transport.send('ep', [`q${threadId}`, 101, type, `u${partnerId}`]);
        // Do not check error here aggressively because it's a push event
        return res;
    }
    async setTyping(threadId, partnerId) {
        return this.setActivity(threadId, partnerId, 0);
    }
    async setUploading(threadId, partnerId) {
        return this.setActivity(threadId, partnerId, 1);
    }
    /** @see stripQmsHighlighting in bbcode/qmsPreprocess.ts */
    static stripHighlighting(text) {
        return (0, qmsPreprocess_1.stripQmsHighlighting)(text);
    }
}
exports.QmsService = QmsService;
